import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, type PortalUserCreateParams, type PortalUserUpdateParams, type UserProduct, type AvailableProduct, type ProductMetadata } from '@/services/user-service';
import { accountService } from '@/services/account-service';
import { accountSettingsService } from '@/services/account-settings-service';
import { affiliateService } from '@/services/affiliate-service';
import { useCurrentUser } from '@/hooks/use-current-user';
import {
  Header,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  ContentContainer,
  Input,
  Label,
  Field,
  FieldError,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Button,
  Card,
  CardContent,
  CardFooter,
  Textarea,
  DatePicker,
  FieldLabel,
  Alert,
  AlertTitle,
  AlertDescription,
  Spinner,
  toast,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  FullPageLoader,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Badge
} from 'prosci-ui';
import { ComboboxTrigger } from '@/components/ui/combobox-trigger';
import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import { usePageContext } from '@/contexts/page-context';
import {
  AlertCircle,
  Mail,
  Package,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Search,
  ShieldCheck,
  Send
} from '@/lib/icons';

// Helper function to format date as YYYY-MM-DD in local timezone
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Read expiration_date as a local calendar date: new Date('YYYY-MM-DD') is UTC
// midnight (the previous day in the Americas), and org-license dates arrive as
// full timestamps.
const parseDateLocal = (value: string): Date => new Date(`${value.slice(0, 10)}T00:00:00`);

// Why a license admin sees an assignment read-only: the organization did not grant it.
const OUTSIDE_LICENSE_LABELS: Record<string, string> = {
  self_purchased: 'Personal subscription',
  program_access: 'Program access',
  not_licensed: 'Not part of your license'
};

export function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: currentAdmin } = useCurrentUser();
  const { setShowPageHeader } = usePageContext();

  // Get return params for preserving list filters
  const returnParams = searchParams.get('return') || '';
  const usersListUrl = returnParams ? `/users?${returnParams}` : '/users';

  const isEditMode = !!(id && id !== 'new');
  const isLicenseAdmin = currentAdmin?.type === 'license_admin';
  const isProsciAdmin = !!(currentAdmin?.level && ['super', 'admin', 'affiliate', 'affiliate_admin', 'data'].includes(currentAdmin.level));
  const isDataLevel = currentAdmin?.type === 'prosci_admin' && currentAdmin?.level === 'data';
  // Mirrors USER_NOTES_ADMIN_LEVELS on the API: plain affiliates share the
  // prosci_admin type but the API neither serializes nor accepts notes for them.
  const isNotesAdmin = !!(currentAdmin?.type === 'prosci_admin' &&
    currentAdmin?.level && ['super', 'admin', 'data', 'affiliate_admin'].includes(currentAdmin.level));
  const isAffiliate = currentAdmin?.level === 'affiliate';
  const isAffiliateAdmin = currentAdmin?.level === 'affiliate_admin';
  // Org seat counts and license expiry only bind admins tied to that license.
  // Prosci staff and affiliates assign outside it, so the limits are
  // informational for them — matching the API, which restricts license admins only.
  const canOverrideLicenseLimits = !isLicenseAdmin;

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  // Form state
  const [formData, setFormData] = useState<PortalUserCreateParams & { account_name?: string }>({
    first_name: '',
    last_name: '',
    email: '',
    title: '',
    customer_type_id: 0, // Start blank - user must select a customer type
    account_id: isLicenseAdmin ? currentAdmin?.account_id : undefined,
    account_name: undefined,
    is_prosci_certified: false,
    product_ids: []
  });

  // Set initial affiliate_id and customer_type for affiliates on new user creation
  useEffect(() => {
    if (!isEditMode && currentAdmin) {
      // For affiliates: lock to their affiliate and set customer_type to Direct
      if (isAffiliate && currentAdmin.affiliate_id) {
        setFormData(prev => ({
          ...prev,
          affiliate_id: currentAdmin.affiliate_id,
          customer_type_id: prev.customer_type_id === 0 ? 1 : prev.customer_type_id // Default to Direct (1)
        }));
      }
      // For affiliate_admins: default to their affiliate but allow changes
      else if (isAffiliateAdmin && currentAdmin.affiliate_id) {
        setFormData(prev => ({
          ...prev,
          affiliate_id: prev.affiliate_id || currentAdmin.affiliate_id
        }));
      }
    }
  }, [isEditMode, currentAdmin, isAffiliate, isAffiliateAdmin]);
  
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [productMetadata, setProductMetadata] = useState<ProductMetadata>({});
  const [enabledFeatures, setEnabledFeatures] = useState<Record<string, Set<string>>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set()); // Start with categories collapsed
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set()); // For individual product expansion
  const [autoAssignedProducts, setAutoAssignedProducts] = useState<Set<string>>(new Set()); // Track auto-assigned products
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false); // Password reset confirmation dialog
  const shouldResendNotificationRef = useRef(false); // Track if we should resend notification email on update

  // Organization search state
  const [orgSearchOpen, setOrgSearchOpen] = useState(false);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [debouncedOrgQuery, setDebouncedOrgQuery] = useState('');
  const orgSearchInputRef = useRef<HTMLInputElement>(null);

  // Debounce organization search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOrgQuery(orgSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [orgSearchQuery]);

  // Fetch user data if editing
  const { data: userData, isLoading: isLoadingUser } = useQuery({
    queryKey: ['portal-user', id],
    queryFn: () => userService.getUser(id!),
    enabled: isEditMode,
    staleTime: 0, // Always refetch when navigating to page or when cache is invalidated
  });

  // Fetch accounts for dropdown (for Prosci admins and data level admins) - searches when query provided
  const { data: accountsData, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ['accounts-form', debouncedOrgQuery],
    queryFn: () => accountService.listAccounts({
      q: debouncedOrgQuery || undefined,
      limit: 50
    }),
    enabled: (isProsciAdmin || isDataLevel) && (orgSearchOpen || debouncedOrgQuery.length > 0),
  });

  // Fetch affiliates for dropdown (only for Prosci admins)
  const { data: affiliatesData } = useQuery({
    queryKey: ['affiliates-form'],
    queryFn: () => affiliateService.listAffiliates(),
    enabled: isProsciAdmin,
  });

  // Fetch available products for create mode based on account/customer type selection
  const { data: availableProductsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['available-products', formData.account_id, formData.customer_type_id],
    queryFn: () => userService.getAvailableProducts({
      account_id: formData.account_id,
      customer_type_id: formData.customer_type_id,
    }),
    enabled: !isEditMode, // Only fetch for create mode
  });

  const { data: orgAccountSettings } = useQuery({
    queryKey: ['account-settings', formData.account_id],
    queryFn: () => accountSettingsService.getSettings(formData.account_id!),
    enabled: !!formData.account_id,
  });

  const orgDisabledFeatureCodes = useMemo(() => {
    const disabled = new Set<string>();
    if (orgAccountSettings?.proxima_api_mode === 'disabled') {
      disabled.add('proxima_api');
    }
    if (formData.account_id && orgAccountSettings && orgAccountSettings.guided_use_cases_mode !== 'per_user') {
      disabled.add('guided_use_cases');
    }
    return disabled;
  }, [orgAccountSettings, formData.account_id]);

  // Effect to handle auto-assigned products in create mode
  useEffect(() => {
    if (!isEditMode && availableProductsData?.data) {
      const autoAssigned = new Set<string>();
      const preSelected = new Set<string>();

      availableProductsData.data.forEach(product => {
        if (product.allow_auto_assignment) {
          autoAssigned.add(product.id);
          preSelected.add(product.id);
        }
      });

      setAutoAssignedProducts(autoAssigned);
      setSelectedProducts(preSelected);
    }
  }, [availableProductsData, isEditMode]);

  // Create/update mutations
  const createMutation = useMutation({
    mutationFn: (data: PortalUserCreateParams) => userService.createUser(data),
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: ['portal-users'] });
      // Send welcome email to newly created user
      if (response?.data?.id) {
        try {
          await userService.sendWelcomeEmail(response.data.id);
          toast.success('User created and welcome email sent');
        } catch {
          // User was created, but email failed - still show success with warning
          toast.success('User created successfully');
          toast.error('Failed to send welcome email');
        }
      }
      navigate(returnParams ? `${usersListUrl}&created=true` : '/users?created=true');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { errors?: string[] | Record<string, string[]>; error?: string }; message?: string; error?: string };
      const newErrors: Record<string, string> = {};
      
      // Handle array of error strings
      if (Array.isArray(err.response?.errors)) {
        err.response.errors.forEach((errMsg: string) => {
          const lowerMsg = errMsg.toLowerCase();
          if (lowerMsg.includes('email')) {
            newErrors.email = errMsg;
          } else if (lowerMsg.includes('first name')) {
            newErrors.first_name = errMsg;
          } else if (lowerMsg.includes('last name')) {
            newErrors.last_name = errMsg;
          } else if (lowerMsg.includes('customer type') || lowerMsg.includes('customer_type')) {
            newErrors.customer_type_id = errMsg;
          } else {
            newErrors.general = errMsg;
          }
        });
      }
      // Handle object with field names as keys
      else if (err.response?.errors && typeof err.response.errors === 'object' && !Array.isArray(err.response.errors)) {
        const errorObj = err.response.errors as Record<string, string[]>;
        Object.keys(errorObj).forEach((key) => {
          const errorMessages = errorObj[key];
          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
            if (key === 'email' || key.includes('email')) {
              newErrors.email = errorMessages[0];
            } else if (key === 'first_name' || key.includes('first_name') || key.includes('first name')) {
              newErrors.first_name = errorMessages[0];
            } else if (key === 'last_name' || key.includes('last_name') || key.includes('last name')) {
              newErrors.last_name = errorMessages[0];
            } else if (key === 'customer_type_id' || key.includes('customer_type') || key.includes('customer type')) {
              newErrors.customer_type_id = errorMessages[0];
            } else {
              newErrors.general = errorMessages[0];
            }
          }
        });
      }
      // Handle single error message
      else if (err.response?.error || err.error || err.message) {
        const errorMsg = err.response?.error || err.error || err.message || 'Failed to create user. Please try again.';
        const lowerMsg = errorMsg.toLowerCase();
        if (lowerMsg.includes('email')) {
          newErrors.email = errorMsg;
        } else if (lowerMsg.includes('first name')) {
          newErrors.first_name = errorMsg;
        } else if (lowerMsg.includes('last name')) {
          newErrors.last_name = errorMsg;
        } else if (lowerMsg.includes('customer type') || lowerMsg.includes('customer_type')) {
          newErrors.customer_type_id = errorMsg;
        } else {
          newErrors.general = errorMsg;
        }
      } else {
        newErrors.general = 'Failed to create user. Please try again.';
      }
      
      setErrors(newErrors);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PortalUserUpdateParams }) =>
      userService.updateUser(id, data),
    onSuccess: async (_data, variables) => {
      // Invalidate and refetch both the list and the specific user query
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['portal-users'] }),
        queryClient.invalidateQueries({ queryKey: ['portal-user', variables.id] }),
      ]);

      // Send welcome email if requested
      if (shouldResendNotificationRef.current) {
        try {
          await userService.sendWelcomeEmail(variables.id);
          toast.success('User updated and notification email sent');
        } catch {
          toast.success('User updated successfully');
          toast.error('Failed to send notification email');
        }
        shouldResendNotificationRef.current = false;
      }

      navigate(returnParams ? `${usersListUrl}&updated=true` : '/users?updated=true');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { errors?: string[] | Record<string, string[]>; error?: string }; message?: string; error?: string };
      const newErrors: Record<string, string> = {};
      
      // Handle array of error strings
      if (Array.isArray(err.response?.errors)) {
        err.response.errors.forEach((errMsg: string) => {
          const lowerMsg = errMsg.toLowerCase();
          if (lowerMsg.includes('email')) {
            newErrors.email = errMsg;
          } else if (lowerMsg.includes('first name')) {
            newErrors.first_name = errMsg;
          } else if (lowerMsg.includes('last name')) {
            newErrors.last_name = errMsg;
          } else if (lowerMsg.includes('customer type') || lowerMsg.includes('customer_type')) {
            newErrors.customer_type_id = errMsg;
          } else {
            newErrors.general = errMsg;
          }
        });
      }
      // Handle object with field names as keys
      else if (err.response?.errors && typeof err.response.errors === 'object' && !Array.isArray(err.response.errors)) {
        const errorObj = err.response.errors as Record<string, string[]>;
        Object.keys(errorObj).forEach((key) => {
          const errorMessages = errorObj[key];
          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
            if (key === 'email' || key.includes('email')) {
              newErrors.email = errorMessages[0];
            } else if (key === 'first_name' || key.includes('first_name') || key.includes('first name')) {
              newErrors.first_name = errorMessages[0];
            } else if (key === 'last_name' || key.includes('last_name') || key.includes('last name')) {
              newErrors.last_name = errorMessages[0];
            } else if (key === 'customer_type_id' || key.includes('customer_type') || key.includes('customer type')) {
              newErrors.customer_type_id = errorMessages[0];
            } else {
              newErrors.general = errorMessages[0];
            }
          }
        });
      }
      // Handle single error message
      else if (err.response?.error || err.error || err.message) {
        const errorMsg = err.response?.error || err.error || err.message || 'Failed to update user. Please try again.';
        const lowerMsg = errorMsg.toLowerCase();
        if (lowerMsg.includes('email')) {
          newErrors.email = errorMsg;
        } else if (lowerMsg.includes('first name')) {
          newErrors.first_name = errorMsg;
        } else if (lowerMsg.includes('last name')) {
          newErrors.last_name = errorMsg;
        } else if (lowerMsg.includes('customer type') || lowerMsg.includes('customer_type')) {
          newErrors.customer_type_id = errorMsg;
        } else {
          newErrors.general = errorMsg;
        }
      } else {
        newErrors.general = 'Failed to update user. Please try again.';
      }
      
      setErrors(newErrors);
    }
  });

  const passwordResetMutation = useMutation({
    mutationFn: (userId: string) => userService.sendPasswordReset(userId),
    onSuccess: (data) => {
      toast.success(data.message || 'Password reset email sent successfully');
    },
    onError: (error: unknown) => {
      const err = error as { error?: string; message?: string };
      const errorMessage = err.error || err.message || 'Failed to send password reset email';
      toast.error(errorMessage);
    }
  });

  // Load user data into form when fetched
  useEffect(() => {
    if (userData?.data?.attributes) {
      const user = userData.data.attributes;
      // Ensure proper type conversion - API may return numbers, but Selects expect strings
      const customerTypeId = typeof user.customer_type_id === 'string'
        ? parseInt(user.customer_type_id, 10)
        : (user.customer_type_id || 0);
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        title: user.title || '',
        customer_type_id: customerTypeId,
        account_id: user.account_id ? String(user.account_id) : undefined,
        account_name: user.account_name || undefined,
        affiliate_id: user.affiliate_id ? String(user.affiliate_id) : undefined,
        // If user has a three_day_certified_date, ensure is_prosci_certified is true
        is_prosci_certified: user.is_prosci_certified || !!user.three_day_certified_date,
        three_day_certified_date: user.three_day_certified_date || undefined,
        ttt_tier: user.ttt_tier || undefined,
        ttt_level: user.ttt_level || undefined,
        notes: user.notes || '',
        product_ids: []
      });
      
      // Set selected products and identify auto-assigned ones
      // Exclude products with past expiration dates (show as unchecked)
      if (user.products) {
        const nonExpiredProducts = user.products.filter(p => {
          if (p.expiration_date && parseDateLocal(p.expiration_date) < new Date()) {
            return false;
          }
          return true;
        });
        setSelectedProducts(new Set(nonExpiredProducts.map(p => p.id)));

        // Load expiration dates into metadata
        const metadata: ProductMetadata = {};
        user.products.forEach(product => {
          metadata[product.id] = {
            expiration_date: product.expiration_date || undefined
          };
        });
        setProductMetadata(metadata);

        // Identify auto-assigned products based on source field or available_products
        const autoAssigned = new Set<string>();
        user.products.forEach(userProduct => {
          // Check if product source indicates auto-assignment
          if (userProduct.source === 'Auto-assigned via organization') {
            autoAssigned.add(userProduct.id);
          }
          // Also check available_products for allow_auto_assignment flag
          else if (user.available_products) {
            const availableProduct = user.available_products.find(ap => ap.id === userProduct.id);
            if (availableProduct?.allow_auto_assignment) {
              autoAssigned.add(userProduct.id);
            }
          }
        });
        setAutoAssignedProducts(autoAssigned);
      }

      // Load product feature assignments
      if (user.product_feature_assignments) {
        const features: Record<string, Set<string>> = {};
        user.product_feature_assignments.forEach(assignment => {
          const key = String(assignment.product_id);
          if (!features[key]) {
            features[key] = new Set();
          }
          features[key].add(assignment.feature_code);
        });
        setEnabledFeatures(features);
      }
    }
  }, [userData]);

  // Products the user has that are outside the current admin's assignable scope
  // These must be preserved when saving to prevent accidental removal
  const unmodifiableProductIds = useMemo(() => {
    if (!isEditMode || !userData?.data?.attributes) return new Set<string>();

    const userProducts = (userData.data.attributes.products || []).map((p: { id: string }) => p.id);
    const availableProducts = (userData.data.attributes.available_products || []).map((p: { id: string }) => p.id);
    const availableSet = new Set(availableProducts);

    // Products user has that admin cannot assign/unassign
    return new Set(userProducts.filter((id: string) => !availableSet.has(id)));
  }, [isEditMode, userData]);

  // Assignments the organization did not grant. License admins see them
  // read-only, and they never count against a seat.
  const outsideLicenseProducts = useMemo<UserProduct[]>(() => {
    if (!isLicenseAdmin || !isEditMode) return [];
    return (userData?.data?.attributes?.products || []).filter(
      (p: UserProduct) => !!p.outside_license_reason
    );
  }, [isLicenseAdmin, isEditMode, userData]);

  const outsideLicenseProductIds = useMemo(
    () => new Set(outsideLicenseProducts.map(p => p.id)),
    [outsideLicenseProducts]
  );

  // Held products that do not appear in the license table at all
  // (e.g. KH Program Materials, Prework), shown separately so the admin can see them.
  const unlicensedHeldProducts = useMemo(() => {
    const availableIds = new Set(
      (userData?.data?.attributes?.available_products || []).map((p: { id: string }) => p.id)
    );
    return outsideLicenseProducts
      .filter(p => !availableIds.has(p.id))
      .filter(p => !(p.expiration_date && parseDateLocal(p.expiration_date) < new Date()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [outsideLicenseProducts, userData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate required fields
    const newErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.customer_type_id || formData.customer_type_id <= 0 || isNaN(formData.customer_type_id)) {
      newErrors.customer_type_id = 'Customer type is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Get available product IDs (only active products)
    const availableProductIds = new Set(
      isEditMode
        ? (userData?.data?.attributes?.available_products || []).map((p: { id: string }) => p.id)
        : (availableProductsData?.data || []).map((p: { id: string }) => p.id)
    );

    // Keep products admin selected from their available list
    const adminSelectedProducts = Array.from(selectedProducts).filter(id => availableProductIds.has(id));
    // Preserve products user already has that admin cannot modify (outside their permission scope)
    const activeSelectedProducts = [...adminSelectedProducts, ...Array.from(unmodifiableProductIds)];

    // Build product metadata with expiration dates and features (only for active products)
    const finalMetadata: ProductMetadata = {};
    activeSelectedProducts.forEach(productId => {
      finalMetadata[productId] = {
        expiration_date: productMetadata[productId]?.expiration_date,
        features: featureCodesForProduct(productId),
        override_org_license: productMetadata[productId]?.override_org_license
      };
    });

    // Exclude account_name from submission - it's read-only (derived from account relationship)
    const { account_name, notes, ...submitData } = formData;

    const dataToSubmit = {
      ...submitData,
      ...(isNotesAdmin ? { notes } : {}),
      product_ids: activeSelectedProducts,
      product_metadata: finalMetadata
    };

    if (isEditMode) {
      await updateMutation.mutateAsync({ id: id!, data: dataToSubmit });
    } else {
      await createMutation.mutateAsync(dataToSubmit);
    }
  };

  const handleProductToggle = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
      // Clear expired expiration date from metadata when re-enabling a product
      // so the old expired date isn't sent back to the API on save
      if (productMetadata[productId]?.expiration_date) {
        const expDate = parseDateLocal(productMetadata[productId].expiration_date!);
        if (expDate < new Date()) {
          setProductMetadata(prev => ({
            ...prev,
            [productId]: { ...prev[productId], expiration_date: null }
          }));
        }
      }
    }
    setSelectedProducts(newSelected);
  };

  const handleExpirationDateChange = (productId: string, date: string | null) => {
    setProductMetadata(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        // Use null to explicitly indicate "cleared", undefined means "not yet set"
        expiration_date: date === null ? null : (date || undefined)
      }
    }));
  };

  // Detach the assignment from the expired org license so it becomes a direct
  // grant. Applied on save, not immediately.
  const handleOrgLicenseOverride = (productId: string) => {
    setProductMetadata(prev => ({
      ...prev,
      [productId]: { ...prev[productId], override_org_license: true }
    }));
  };

  const featureOwnerKey = (declaringProductId: string | undefined, panelProductId: string) =>
    String(declaringProductId ?? panelProductId);

  const featureCodesForProduct = (productId: string) => {
    const source = isEditMode
      ? (userData?.data?.attributes?.available_products || [])
      : (availableProductsData?.data || []);
    const product = source.find(p => String(p.id) === String(productId));

    // A product outside the admin's scope offers no panel, so there is nothing
    // to reconcile against — submit what the user already holds rather than an
    // empty list, which the API would read as a revocation.
    if (!product) {
      return Array.from(enabledFeatures[String(productId)] || []);
    }

    return (product.product_features || [])
      .filter(feature => enabledFeatures[featureOwnerKey(feature.product_id, productId)]?.has(feature.code))
      .map(feature => feature.code);
  };

  const handleFeatureToggle = (productId: string, featureCode: string) => {
    const key = String(productId);
    setEnabledFeatures(prev => {
      const productFeatures = new Set(prev[key] || []);
      if (productFeatures.has(featureCode)) {
        productFeatures.delete(featureCode);
      } else {
        productFeatures.add(featureCode);
      }
      return {
        ...prev,
        [key]: productFeatures
      };
    });
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleProduct = (productId: string) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  // Group products by category for display with table layout
  const renderProductsByCategory = (products: AvailableProduct[], assignedProducts: UserProduct[]) => {
    // Create a map of assigned products for quick lookup
    const assignedMap = new Map(assignedProducts.map(p => [p.id, p]));
    
    // Group products by their category field from the database - matching legacy PHP structure exactly
    const grouped = products.reduce((acc, product) => {
      // Use the category directly from the database
      const category = product.category || 'other';

      if (!acc[category]) acc[category] = [];
      acc[category].push(product);
      return acc;
    }, {} as Record<string, AvailableProduct[]>);

    // Determine if we should show the seats column
    // Show for license admins always, or for Prosci admins editing license admins/users (customer_type 2 or 3)
    const isEditingLicenseUser = [2, 3].includes(formData.customer_type_id || 0);
    const showSeatsColumn = isLicenseAdmin || (isProsciAdmin && isEditingLicenseUser);

    // Render a subcategory table
    const renderSubcategoryTable = (title: string, categoryId: string, categoryProducts: AvailableProduct[], level: number = 0) => {
      if (!categoryProducts || categoryProducts.length === 0) return null;

      // Sort products alphabetically by name
      const sortedProducts = [...categoryProducts].sort((a, b) => a.name.localeCompare(b.name));

      const isExpanded = expandedCategories.has(categoryId);
      const marginLeft = level > 0 ? 'ml-8' : '';
      
      return (
        <div key={categoryId} className={marginLeft}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Button
                    type="button"
                    onClick={() => toggleCategory(categoryId)}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>
                  {title}
                </TableHead>
                <TableHead className="text-center w-32">
                  Assigned
                </TableHead>
                {showSeatsColumn && (
                  <TableHead className="text-center w-32">
                    Seats Used
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
          </Table>
          {isExpanded && (
            <div className="ml-8">
              <Table>
                <TableBody>
                {sortedProducts.map((product) => {
                  const isAssigned = selectedProducts.has(product.id);
                  const isAutoAssigned = autoAssignedProducts.has(product.id);
                  const assignedProduct = assignedMap.get(product.id);
                  const hasNoSeats = product.available_seats !== null && product.available_seats <= 0 && !isAssigned;
                  const isOverLimit = product.available_seats !== null && product.available_seats < 0;
                  const isProductExpanded = expandedProducts.has(product.id);

                  // Check if user's product assignment is expired
                  const userProductExpired = assignedProduct?.expiration_date &&
                                            parseDateLocal(assignedProduct.expiration_date) < new Date();

                  // Check if org license is expired (for license admins)
                  const orgLicenseExpired = product.org_license_expired;

                  // Assigned to this user, but the expired org license it is linked
                  // to voids access until the grant is made direct.
                  const grantBlocked = isAssigned && !!assignedProduct?.org_license_blocked;
                  const overrideRequested = !!productMetadata[product.id]?.override_org_license;

                  // Auto-assigned products cannot be deselected by any admin.
                  // Seat exhaustion and org license expiry only block admins bound
                  // to the organization license.
                  // Assignments the organization did not grant are read-only for
                  // license admins and never take a seat.
                  const outsideLicenseReason = isLicenseAdmin && isAssigned
                    ? assignedProduct?.outside_license_reason ?? null
                    : null;
                  const isDisabled = isAutoAssigned || !!outsideLicenseReason ||
                                    (!canOverrideLicenseLimits &&
                                      ((hasNoSeats && !product.allow_auto_assignment) || orgLicenseExpired));
                  
                  return (
                    <React.Fragment key={product.id}>
                      <TableRow>
                        <TableCell className="w-12">
                          {(isProsciAdmin || isDataLevel) && isAssigned && (
                            <Button
                              type="button"
                              onClick={() => toggleProduct(product.id)}
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                            >
                              {isProductExpanded ? (
                                <ChevronDown className="h-3 w-3" />
                              ) : (
                                <ChevronRight className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`product-${product.id}`}
                              checked={isAssigned}
                              onCheckedChange={() => !isDisabled && handleProductToggle(product.id)}
                              disabled={isDisabled}
                            />
                            <Label 
                              htmlFor={`product-${product.id}`}
                              className={`text-sm ${hasNoSeats || outsideLicenseReason ? 'text-gray-400' : ''} ${isDisabled ? '' : 'cursor-pointer'}`}
                            >
                              {product.name}
                            </Label>
                            {/* Show auto-assigned badge - these cannot be deselected */}
                            {isAutoAssigned && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Auto-assigned
                              </span>
                            )}
                            {outsideLicenseReason && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                                title="This user has this outside your organization's license. It does not use a seat."
                              >
                                {OUTSIDE_LICENSE_LABELS[outsideLicenseReason] ?? 'Outside your license'}
                              </span>
                            )}
                          </div>
                          {/* Show related products info - matching legacy PHP app display */}
                          {product.is_grouped_product && product.related_products && product.related_products.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                              By enabling {product.name}, this user will get access to{' '}
                              <HoverCardPrimitive.Root>
                                <HoverCardPrimitive.Trigger asChild>
                                  <span className="underline cursor-help">
                                    these resources
                                  </span>
                                </HoverCardPrimitive.Trigger>
                                <HoverCardPrimitive.Portal>
                                  <HoverCardPrimitive.Content 
                                    className="z-50 w-[380px] rounded-md border bg-white p-4 shadow-md"
                                    align="start"
                                    sideOffset={4}
                                  >
                                    <p className="text-sm font-semibold mb-3">
                                      {product.name} includes:
                                    </p>
                                    <Table>
                                      <TableBody>
                                        {product.related_products.map((resource, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell className="py-2 pr-4 text-xs">{resource.name}</TableCell>
                                            <TableCell className="py-2 text-xs text-muted-foreground text-right">
                                              {resource.group || 'Knowledge Hub'}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </HoverCardPrimitive.Content>
                                </HoverCardPrimitive.Portal>
                              </HoverCardPrimitive.Root>
                            </p>
                          )}
                          {/* Product feature checkboxes */}
                          {isAssigned && product.product_features && product.product_features.length > 0 && (
                            <div className="ml-6 mt-2 space-y-1">
                              {product.product_features.map((feature) => {
                                // Check if current admin can see this feature
                                const canSeeFeature = isProsciAdmin ||
                                  (feature.settings?.admin_level &&
                                   feature.settings.admin_level.includes(currentAdmin?.customer_type_id || 0)) ||
                                  (feature.code === 'proxima_api' && orgAccountSettings?.proxima_api_mode === 'per_user') ||
                                  (feature.code === 'guided_use_cases' && orgAccountSettings?.guided_use_cases_mode === 'per_user');

                                if (!canSeeFeature) return null;
                                if (orgDisabledFeatureCodes.has(feature.code)) return null;

                                const owningProductId = featureOwnerKey(feature.product_id, product.id);
                                const isFeatureEnabled = enabledFeatures[owningProductId]?.has(feature.code) || false;

                                return (
                                  <div key={feature.code} className="flex items-center gap-2">
                                    <Checkbox
                                      id={`feature-${product.id}-${feature.code}`}
                                      checked={isFeatureEnabled}
                                      onCheckedChange={() => handleFeatureToggle(owningProductId, feature.code)}
                                      disabled={isDisabled}
                                    />
                                    <Label
                                      htmlFor={`feature-${product.id}-${feature.code}`}
                                      className="cursor-pointer"
                                    >
                                      {feature.name}
                                    </Label>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Warning messages */}
                          {userProductExpired && assignedProduct?.expiration_date && (
                            <p className="text-xs text-destructive mt-1 ml-6">
                              Inactivated by: Expired on {parseDateLocal(assignedProduct.expiration_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </p>
                          )}
                          {grantBlocked && canOverrideLicenseLimits && (
                            <div className="flex flex-wrap items-center gap-2 mt-1 ml-6">
                              <p className="text-xs text-destructive">
                                Access blocked: this assignment is tied to the organization license, which has expired.
                              </p>
                              {overrideRequested ? (
                                <span className="text-xs text-muted-foreground">
                                  Will be assigned directly to this user on save
                                </span>
                              ) : (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-xs"
                                  onClick={() => handleOrgLicenseOverride(product.id)}
                                >
                                  Assign directly to this user
                                </Button>
                              )}
                            </div>
                          )}
                          {orgLicenseExpired && product.org_license_expiration_date && !(grantBlocked && canOverrideLicenseLimits) && (
                            canOverrideLicenseLimits ? (
                              <p className="text-xs text-muted-foreground mt-1 ml-6">
                                Organization license for this tool expired on {new Date(product.org_license_expiration_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. You can still assign it directly to this user.
                              </p>
                            ) : (
                              <p className="text-xs text-destructive mt-1 ml-6">
                                Your account's access to this tool expired on {new Date(product.org_license_expiration_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Contact your Prosci Account Manager to renew access.
                              </p>
                            )
                          )}
                          {hasNoSeats && !product.allow_auto_assignment && (
                            canOverrideLicenseLimits ? (
                              <p className="text-xs text-muted-foreground mt-1 ml-6">
                                No seats remaining on the organization license. Assigning this tool will exceed the purchased seats.
                              </p>
                            ) : (
                              <p className="text-xs text-destructive mt-1 ml-6">
                                No seats available
                              </p>
                            )
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {isAssigned && assignedProduct && assignedProduct.created_at && !isAutoAssigned ? (
                            new Date(assignedProduct.created_at).toLocaleDateString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric'
                            })
                          ) : (
                            <span>—</span>
                          )}
                        </TableCell>
                        {showSeatsColumn && (
                          <TableCell className="text-center text-sm">
                            {product.available_seats !== null && product.total_seats !== null ? (
                              <div className={isOverLimit ? 'text-destructive font-medium' : ''}>
                                {product.total_seats - product.available_seats} / {product.total_seats}
                                {isOverLimit && (
                                  <div className="text-xs">
                                    {Math.abs(product.available_seats)} over
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span>—</span>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                      {/* Expandable row for expiration date (Prosci admins only, including data level) */}
                      {(isProsciAdmin || isDataLevel) && isProductExpanded && (
                        <TableRow>
                          <TableCell colSpan={showSeatsColumn ? 4 : 3} className="bg-gray-50 px-12 py-3">
                            <Field className="flex items-center gap-3 space-y-0">
                              <Label htmlFor={`expiration-${product.id}`}>
                                Expiration Date:
                              </Label>
                              {(() => {
                                // Determine the effective expiration date:
                                // - If metadata has been explicitly set (including to null), use that
                                // - Otherwise fall back to the original assignedProduct value
                                const metadataHasBeenSet = product.id in productMetadata && 'expiration_date' in (productMetadata[product.id] || {});
                                const effectiveDate = metadataHasBeenSet
                                  ? productMetadata[product.id]?.expiration_date
                                  : assignedProduct?.expiration_date;

                                return (
                                  <>
                                    <DatePicker
                                      id={`expiration-${product.id}`}
                                      date={effectiveDate ? new Date(effectiveDate + 'T00:00:00') : undefined}
                                      onDateChange={(date: Date | undefined) =>
                                        handleExpirationDateChange(product.id, date ? formatDateLocal(date) : null)
                                      }
                                      placeholder="Select date"
                                      captionLayout="dropdown"
                                      fromYear={2020}
                                      toYear={new Date().getFullYear() + 10}
                                      className="w-auto"
                                    />
                                    {effectiveDate && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                        onClick={() => handleExpirationDateChange(product.id, null)}
                                        title="Remove expiration date"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </>
                                );
                              })()}
                            </Field>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      );
    };

    // Table-style category header (like subcategory tables but expands to show nested content)
    const renderTableCategoryHeader = (title: string, categoryId: string, children: React.ReactNode) => {
      const isExpanded = expandedCategories.has(categoryId);
      
      return (
        <div key={categoryId}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Button
                    type="button"
                    onClick={() => toggleCategory(categoryId)}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>
                  {title}
                </TableHead>
                <TableHead className="text-center w-32">
                  Assigned
                </TableHead>
                {showSeatsColumn && (
                  <TableHead className="text-center w-32">
                    Seats Used
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
          </Table>
          {isExpanded && (
            <div className="ml-8">
              {children}
            </div>
          )}
        </div>
      );
    };

    // Define all explicitly handled category keys
    const handledCategories = new Set([
      'cont-kh', 'cont-rh', 'cont-ih',
      'cont-elrn',
      'cont-elrn-en', 'cont-elrn-pt', 'cont-elrn-es',
      'cont-elrn-fr', 'cont-elrn-de', 'cont-elrn-ja', 'cont-elrn-zh',
      'cont-advtrk', 'rsrc', 'cont',
      'apps', 'lgy', 'lgy-rsrc', 'lgy-web',
      'bundles', 'other'
    ]);

    // Find any categories that aren't explicitly handled
    const unhandledCategories = Object.keys(grouped).filter(cat => !handledCategories.has(cat));
    const unhandledProducts = unhandledCategories.flatMap(cat => grouped[cat]);

    // Check if we have products in main categories
    const hasContent = grouped['cont-kh'] || grouped['cont-rh'] || grouped['cont-ih'] ||
                      grouped['cont-elrn'] || grouped['cont-advtrk'] || grouped['rsrc'] || grouped['cont'];
    const hasApps = grouped['apps'] || grouped['lgy'] || grouped['lgy-rsrc'] || grouped['lgy-web'];
    const hasBundles = grouped['bundles'];
    const hasOther = grouped['other'];
    const hasUnhandled = unhandledProducts.length > 0;

    return (
      <div>
        {hasContent && renderTableCategoryHeader('Content', 'content',
          <>
            {renderSubcategoryTable('Knowledge Hub', 'kh', grouped['cont-kh'] || [], 0)}
            {renderSubcategoryTable('Research Hub', 'rh', grouped['cont-rh'] || [], 0)}
            {renderSubcategoryTable('Instructor Hub', 'ih', grouped['cont-ih'] || [], 0)}
            {renderSubcategoryTable('eLearning', 'elearning', grouped['cont-elrn'] || [], 0)}
            {renderSubcategoryTable('Advanced Tracks', 'advtrk', grouped['cont-advtrk'] || [], 0)}
            {renderSubcategoryTable('Resources', 'rsrc', grouped['rsrc'] || [], 0)}
            {renderSubcategoryTable('Other Content', 'cont-oth', grouped['cont'] || [], 0)}
          </>
        )}
        {hasApps && renderTableCategoryHeader('Apps', 'apps',
          <>
            {renderSubcategoryTable('Apps', 'apps-products', grouped['apps'] || [], 0)}
            {(grouped['lgy']?.length || grouped['lgy-rsrc']?.length || grouped['lgy-web']?.length) ?
              renderTableCategoryHeader('Legacy', 'legacy',
                <>
                  {renderSubcategoryTable('Legacy', 'lgy', grouped['lgy'] || [], 0)}
                  {renderSubcategoryTable('Resources', 'lgy-rsrc', grouped['lgy-rsrc'] || [], 0)}
                  {renderSubcategoryTable('Webinars', 'lgy-web', grouped['lgy-web'] || [], 0)}
                </>
              ) : null}
          </>
        )}
        {hasBundles && renderSubcategoryTable('Product Suites', 'bundles', grouped['bundles'] || [], 0)}
        {hasOther && renderSubcategoryTable('Other Resources', 'other', grouped['other'] || [], 0)}
        {/* Render any products in unhandled categories */}
        {hasUnhandled && renderSubcategoryTable('Additional Products', 'unhandled', unhandledProducts, 0)}
      </div>
    );
  };

  if (isLoadingUser) {
    return <FullPageLoader message="Loading user details..." />;
  }

  // Get available products based on user type and account
  const availableProducts = isEditMode
    ? (userData?.data?.attributes?.available_products || [])
    : (availableProductsData?.data || []);

  return (
    <>
      <Header 
        title={isEditMode ? 'Edit User' : 'New User'}
        size="small"
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={usersListUrl}>Users</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{isEditMode ? 'Edit User' : 'New User'}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      >
        {isEditMode && userData?.data?.attributes && (
          userData.data.attributes.account?.uses_saml_login ? (
            <Badge variant="primary" tint>
              <ShieldCheck className="h-3 w-3" />
              SSO Enabled
            </Badge>
          ) : (
            (userData.data.attributes.available_actions?.includes('reset_password') ?? true) && (
              <Button
                type="button"
                onClick={() => setShowPasswordResetDialog(true)}
                disabled={passwordResetMutation.isPending}
                variant="lilac"
              >
                {passwordResetMutation.isPending ? (
                  <>
                    <Spinner size="small" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail />
                    Send Password Reset
                  </>
                )}
              </Button>
            )
          )
        )}
      </Header>
      <ContentContainer>

      {/* Error display */}
      {errors.general && (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errors.general}</AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Basic Information */}
        <Card>
          <CardContent>
            <h2 className="text-3xl font-normal mb-4 font-serif">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field>
                <Label htmlFor="firstName" required>
                  First Name
                </Label>
                <Input
                  type="text"
                  id="firstName"
                  value={formData.first_name}
                  onChange={(e) => {
                    setFormData({ ...formData, first_name: e.target.value });
                    if (errors.first_name && e.target.value.trim()) {
                      const newErrors = { ...errors };
                      delete newErrors.first_name;
                      setErrors(newErrors);
                    }
                  }}
                  aria-invalid={!!errors.first_name}
                />
                {errors.first_name && (
                  <FieldError>{errors.first_name}</FieldError>
                )}
              </Field>

              <Field>
                <Label htmlFor="lastName" required>
                  Last Name
                </Label>
                <Input
                  type="text"
                  id="lastName"
                  value={formData.last_name}
                  onChange={(e) => {
                    setFormData({ ...formData, last_name: e.target.value });
                    if (errors.last_name && e.target.value.trim()) {
                      const newErrors = { ...errors };
                      delete newErrors.last_name;
                      setErrors(newErrors);
                    }
                  }}
                  aria-invalid={!!errors.last_name}
                />
                {errors.last_name && (
                  <FieldError>{errors.last_name}</FieldError>
                )}
              </Field>

              <Field>
                <Label htmlFor="email" required>
                  Email
                </Label>
                <Input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (errors.email && e.target.value.trim()) {
                      const newErrors = { ...errors };
                      delete newErrors.email;
                      setErrors(newErrors);
                    }
                  }}
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <FieldError>{errors.email}</FieldError>
                )}
              </Field>

              <Field>
                <Label htmlFor="title">
                  Title
                </Label>
                <Input
                  type="text"
                  id="title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </Field>

              {/* Organization field - editable for Prosci admins and data level admins, hidden for license admins */}
              {!isLicenseAdmin && (
                <Field>
                  <Label htmlFor="organization">
                    Organization
                  </Label>
                  {(isProsciAdmin || isDataLevel) ? (
                    <Popover open={orgSearchOpen} onOpenChange={setOrgSearchOpen}>
                      <PopoverTrigger asChild>
                        <ComboboxTrigger
                          role="combobox"
                          aria-expanded={orgSearchOpen}
                          id="organization"
                          value={formData.account_id && formData.account_name ? formData.account_name : undefined}
                          placeholder="Select Organization..."
                        />
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <div className="flex flex-col">
                          <div className="flex items-center border-b px-3">
                            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                            <input
                              ref={orgSearchInputRef}
                              placeholder="Search organizations..."
                              value={orgSearchQuery}
                              onChange={(e) => setOrgSearchQuery(e.target.value)}
                              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                            />
                            {orgSearchQuery && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => setOrgSearchQuery('')}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <div className="max-h-[300px] overflow-y-auto">
                            {/* No Organization option */}
                            <div
                              className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                              onClick={() => {
                                setFormData({ ...formData, account_id: undefined, account_name: undefined });
                                setOrgSearchOpen(false);
                                setOrgSearchQuery('');
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${!formData.account_id ? 'opacity-100' : 'opacity-0'}`} />
                              None Selected
                            </div>
                            {/* Separator */}
                            <div className="h-px bg-border my-1" />
                            {/* Loading state */}
                            {isLoadingAccounts && (
                              <div className="py-6 text-center text-sm text-muted-foreground">
                                <Spinner size="small" />
                                <span className="ml-2">Searching...</span>
                              </div>
                            )}
                            {/* No results */}
                            {!isLoadingAccounts && accountsData?.data?.length === 0 && orgSearchQuery && (
                              <div className="py-6 text-center text-sm text-muted-foreground">
                                No organizations found.
                              </div>
                            )}
                            {/* Search prompt */}
                            {!isLoadingAccounts && !accountsData?.data?.length && !orgSearchQuery && (
                              <div className="py-6 text-center text-sm text-muted-foreground">
                                Type to search organizations...
                              </div>
                            )}
                            {/* Account list */}
                            {accountsData?.data?.map(account => (
                              <div
                                key={account.id}
                                className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    account_id: account.id,
                                    account_name: account.attributes.name
                                  });
                                  setOrgSearchOpen(false);
                                  setOrgSearchQuery('');
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${formData.account_id === account.id ? 'opacity-100' : 'opacity-0'}`} />
                                <span className="flex-1">{account.attributes.name}</span>
                                <span className="ml-2 text-xs text-muted-foreground">id: {account.id}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <p className="px-3 py-2 bg-gray-50 rounded-md text-sm">
                      {formData.account_name || 'Not assigned'}
                    </p>
                  )}
                </Field>
              )}
            </div>

            <hr className="my-8" />

            {/* Account Settings */}
            <h2 className="text-3xl font-normal mb-4 font-serif">Account Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field>
                <Label htmlFor="customerType" required>
                  Customer Type
                </Label>
                <Select
                  value={(() => {
                    // Get the customer type ID - prefer formData (for user changes), fall back to userData
                    const ctId = formData.customer_type_id || userData?.data?.attributes?.customer_type_id;
                    return ctId && ctId > 0 ? String(ctId) : undefined;
                  })()}
                  onValueChange={(value) => {
                    if (value && value.trim() !== '') {
                      const parsedValue = parseInt(value);
                      if (!isNaN(parsedValue) && parsedValue > 0) {
                        setFormData({ ...formData, customer_type_id: parsedValue });
                        // Clear error when user selects a valid value
                        if (errors.customer_type_id) {
                          const newErrors = { ...errors };
                          delete newErrors.customer_type_id;
                          setErrors(newErrors);
                        }
                      }
                    } else {
                      // Set to 0 to trigger validation error
                      setFormData({ ...formData, customer_type_id: 0 });
                    }
                  }}
                  disabled={isLicenseAdmin && isEditMode && id === currentAdmin?.id}
                >
                  <SelectTrigger id="customerType" className="w-full" aria-invalid={!!errors.customer_type_id}>
                    <SelectValue placeholder="Select customer type" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLicenseAdmin ? (
                      <>
                        <SelectItem value="2">License Admin</SelectItem>
                        <SelectItem value="3">License User</SelectItem>
                      </>
                    ) : isAffiliate ? (
                      // Affiliates can only create Direct users
                      <SelectItem value="1">Direct</SelectItem>
                    ) : (
                      <>
                        <SelectItem value="1">Direct</SelectItem>
                        <SelectItem value="2">License Admin</SelectItem>
                        <SelectItem value="3">License User</SelectItem>
                        <SelectItem value="4">Enterprise Onsite</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                {errors.customer_type_id && (
                  <FieldError>{errors.customer_type_id}</FieldError>
                )}
                {isLicenseAdmin && isEditMode && id === currentAdmin?.id && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    You cannot change your own customer type
                  </p>
                )}
              </Field>

              {isProsciAdmin && (
                <Field>
                  <Label htmlFor="affiliate">
                    Affiliate
                  </Label>
                  <Select
                    value={(() => {
                      // Get affiliate ID - prefer formData (for user changes), fall back to userData
                      const affId = formData.affiliate_id || userData?.data?.attributes?.affiliate_id;
                      return affId ? String(affId) : '__none__';
                    })()}
                    onValueChange={(value) => setFormData({ ...formData, affiliate_id: value === '__none__' ? undefined : value })}
                    disabled={isAffiliate} // Affiliates cannot change the affiliate
                  >
                    <SelectTrigger id="affiliate" className="w-full">
                      <SelectValue placeholder="No Affiliate" />
                    </SelectTrigger>
                    <SelectContent>
                      {!isAffiliate && <SelectItem value="__none__">No Affiliate</SelectItem>}
                      {affiliatesData?.data
                        ?.slice() // Create a copy to avoid mutating original
                        ?.sort((a, b) => a.attributes.name.localeCompare(b.attributes.name))
                        ?.map(affiliate => (
                          <SelectItem key={String(affiliate.id)} value={String(affiliate.id)}>
                            {affiliate.attributes.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {isAffiliate && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      Users you create are automatically assigned to your affiliate
                    </p>
                  )}
                </Field>
              )}
            </div>

            <hr className="my-8" />
            
            {/* Certifications */}
            <h2 className="text-3xl font-normal mb-4 font-serif">Certifications</h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-3">
                    <Checkbox
                      id="is_prosci_certified"
                      checked={formData.is_prosci_certified}
                      onCheckedChange={(checked) => {
                        const certified = checked === true;
                        // null, not undefined — undefined is dropped by JSON.stringify, so the
                        // stale date would survive the PATCH and re-check the box on reload.
                        setFormData({
                          ...formData,
                          is_prosci_certified: certified,
                          three_day_certified_date: certified ? formData.three_day_certified_date : null
                        });
                      }}
                   />
                  <Label htmlFor="is_prosci_certified" className="text-sm font-medium  cursor-pointer">
                    3-Day Certified
                  </Label>
                </div>
                {formData.is_prosci_certified && (
                  <Field className="mt-4 ml-[28px] flex flex-col gap-0">
                    <FieldLabel htmlFor="certDate">
                      3-Day Graduation Date
                    </FieldLabel>
                    <DatePicker
                      id="certDate"
                      date={formData.three_day_certified_date ? new Date(formData.three_day_certified_date + 'T00:00:00') : undefined}
                      onDateChange={(date: Date | undefined) => setFormData({ ...formData, three_day_certified_date: date ? formatDateLocal(date) : null })}
                      placeholder="Select date"
                      captionLayout="dropdown"
                      fromYear={2010}
                      toYear={new Date().getFullYear()}
                      className="w-full max-w-xs"
                    />
                  </Field>
                )}
              </div>
              
          </div>

          {isEditMode && userData?.data?.attributes?.credly_badge?.opted_in && (
            <>
              <hr className="my-8" />
              <h2 className="text-3xl font-normal font-serif">Prosci Member Badge</h2>
              <p className="text-sm text-muted-foreground mt-1">Credly badge status (read-only)</p>
              {userData?.data?.attributes?.credly_badge?.image_url && (
                <a
                  href={userData?.data?.attributes?.credly_badge?.badge_url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-4"
                >
                  <img
                    src={userData.data.attributes.credly_badge.image_url}
                    alt="Prosci Member badge"
                    className="h-20 w-20"
                  />
                </a>
              )}
              <dl className="mt-4 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm max-w-md">
                <dt className="text-muted-foreground">Status</dt>
                <dd className="font-medium">{userData?.data?.attributes?.credly_badge?.state || 'pending'}</dd>
                <dt className="text-muted-foreground">Email</dt>
                <dd className="font-medium">{userData?.data?.attributes?.credly_badge?.email || '—'}</dd>
                <dt className="text-muted-foreground">Expires</dt>
                <dd className="font-medium">{userData?.data?.attributes?.credly_badge?.expires_at || '—'}</dd>
                {userData?.data?.attributes?.credly_badge?.sync_error && (
                  <>
                    <dt className="text-muted-foreground">Last error</dt>
                    <dd className="font-medium text-destructive">{userData.data.attributes.credly_badge.sync_error}</dd>
                  </>
                )}
                <dt className="text-muted-foreground">Credly</dt>
                <dd className="font-medium">
                  {userData?.data?.attributes?.credly_badge?.badge_url ? (
                    <a href={userData.data.attributes.credly_badge.badge_url} target="_blank" rel="noopener noreferrer" className="underline">
                      View badge
                    </a>
                  ) : '—'}
                </dd>
              </dl>
            </>
          )}

          <hr className="my-8" />

          {/* Product Assignment / Licenses */}
          <h2 className="text-3xl font-normal font-serif">Licenses & Tools</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isEditMode
              ? 'Manage access to products and tools for this user'
              : 'Select which products and tools to assign to this user'}
          </p>
          
          <div className="flex items-center justify-between">
            {availableProducts.length > 0 && (
              <div className="flex items-center gap-2 text-sm mt-4">
                <span className="text-muted-foreground">Total assigned:</span>
                <span className="font-medium text-muted-foreground">
                  {availableProducts.filter(p => selectedProducts.has(p.id) && !outsideLicenseProductIds.has(p.id)).length} / {availableProducts.length}
                </span>
              </div>
            )}
          </div>
            
          {/* Seat warning for license admins */}
          {isLicenseAdmin && availableProducts.some(p => p.available_seats !== null && p.available_seats < 0) && (
            <Alert variant="warning" className="mt-4">
              <AlertCircle />
              <AlertTitle>Seat Limit Exceeded</AlertTitle>
              <AlertDescription>
                One or more products are over their seat limit. Contact your account manager to purchase additional seats.
              </AlertDescription>
            </Alert>
          )}
          
          <div>
            {!isEditMode && isLoadingProducts ? (
              <div className="text-center py-8">
                <Spinner size="medium" />
                <p className="mt-2">Loading available products...</p>
              </div>
            ) : availableProducts.length === 0 ? (
              <div className="text-center py-8 mt-8 bg-neutral-50 rounded-lg">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p>No products available for assignment</p>
              </div>
            ) : (
              <div>
                {/* Auto-assigned products list */}
                {autoAssignedProducts.size > 0 && (
                  <div className="mb-4 mt-8">
                    <p className="text-sm font-medium mb-2">Automatically assigned via license:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {Array.from(autoAssignedProducts).map(productId => {
                        const product = availableProducts.find(p => p.id === productId);
                        return product ? (
                          <li key={productId} className="text-sm font-semibold">
                            {product.name}
                          </li>
                        ) : null;
                      })}
                    </ul>
                  </div>
                )}

                <p className="text-sm font-medium mb-2">Assign available seats:</p>

                {/* Group products by type */}
                <div className="mt-4 border-t">
                {renderProductsByCategory(availableProducts, userData?.data?.attributes?.products || [])}
                </div>

                {/* Tools the user holds that this organization's license does not cover */}
                {unlicensedHeldProducts.length > 0 && (
                  <div className="mt-8">
                    <p className="text-sm font-medium mb-1">Tools held outside your organization's license:</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      These come from Prosci directly, not from your organization's license. They don't use a seat and can't be changed here.
                    </p>
                    <ul className="space-y-2">
                      {unlicensedHeldProducts.map(product => (
                        <li key={product.id} className="flex items-center gap-2">
                          <Checkbox id={`outside-product-${product.id}`} checked disabled />
                          <Label htmlFor={`outside-product-${product.id}`} className="text-sm text-gray-400">
                            {product.name}
                          </Label>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            {OUTSIDE_LICENSE_LABELS[product.outside_license_reason ?? ''] ?? 'Outside your license'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

            {/* Additional Settings */}
            {isNotesAdmin && (
              <>
                <hr className="my-8" />
                <h2 className="text-3xl font-normal mb-4 font-serif">Additional Settings</h2>
                <Field>
                  <Label htmlFor="notes">
                    Notes (Admin Only)
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes || ''}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </Field>
              </>
            )}
          </CardContent>
          <CardFooter className="flex justify-end gap-4">
            <Button
              asChild
              variant="outline"
            >
              <Link to={usersListUrl}>
                Cancel
              </Link>
            </Button>
            {isEditMode && !userData?.data?.attributes?.account?.supresses_emails && (
              <Button
                type="button"
                variant="lilac"
                disabled={createMutation.isPending || updateMutation.isPending}
                onClick={() => {
                  shouldResendNotificationRef.current = true;
                  // Trigger form submission programmatically
                  const form = document.querySelector('form');
                  if (form) {
                    form.requestSubmit();
                  }
                }}
              >
                {updateMutation.isPending && shouldResendNotificationRef.current ? (
                  <>
                    <Spinner size="small" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Update and Resend Notification
                  </>
                )}
              </Button>
            )}
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              onClick={() => {
                // Reset the resend notification flag when using normal submit
                shouldResendNotificationRef.current = false;
              }}
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <>
                  <Spinner size="small" color="light" />
                  Saving...
                </>
              ) : (
                <>
                  {isEditMode ? 'Update User' : 'Create User'}
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>

        {/* Password Reset Confirmation Dialog */}
        <Dialog open={showPasswordResetDialog} onOpenChange={setShowPasswordResetDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Password Reset</DialogTitle>
              <DialogDescription>
                Are you sure you want to send a password reset email to {userData?.data?.attributes?.email}?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => setShowPasswordResetDialog(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  passwordResetMutation.mutate(id!);
                  setShowPasswordResetDialog(false);
                }}
              >
                Send Reset Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </ContentContainer>
    </>
  );
}